import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Send, Loader2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const initial = { nombre: "", telefono: "", edad: "", motivo: "" };

export default function LeadForm({ onSuccess, t }) {
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.motivo) {
      toast.error(t.errorMotivo);
      return;
    }
    setLoading(true);
    try {
      await api.post("/leads", {
        nombre: form.nombre.trim(),
        telefono: form.telefono.trim(),
        edad: Number(form.edad),
        motivo: form.motivo,
      });
      toast.success(t.successToast);
      onSuccess(form.nombre.trim().split(" ")[0]);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <form
      onSubmit={submit}
      data-testid="lead-form"
      className="bg-white rounded-3xl border border-line p-6 sm:p-10 shadow-[0_30px_70px_-40px_rgba(28,36,30,0.35)]"
    >
      <h3 className="font-serif text-2xl sm:text-3xl font-light mb-2">{t.title}</h3>
      <p className="text-sm text-stonemute mb-8">{t.sub}</p>

      <div className="space-y-5">
        <div>
          <Label htmlFor="nombre" className="text-xs uppercase tracking-[0.2em] font-bold text-ink/70">{t.nombre}</Label>
          <Input id="nombre" data-testid="lead-nombre-input" required minLength={2} value={form.nombre} onChange={set("nombre")}
            placeholder={t.nombrePh} className="mt-2 h-12 bg-sand/50 border-line focus-visible:ring-sage" />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <div>
            <Label htmlFor="telefono" className="text-xs uppercase tracking-[0.2em] font-bold text-ink/70">{t.telefono}</Label>
            <Input id="telefono" data-testid="lead-telefono-input" required type="tel" minLength={7} value={form.telefono} onChange={set("telefono")}
              placeholder={t.telefonoPh} className="mt-2 h-12 bg-sand/50 border-line focus-visible:ring-sage" />
          </div>
          <div>
            <Label htmlFor="edad" className="text-xs uppercase tracking-[0.2em] font-bold text-ink/70">{t.edad}</Label>
            <Input id="edad" data-testid="lead-edad-input" required type="number" min={1} max={110} value={form.edad} onChange={set("edad")}
              placeholder={t.edadPh} className="mt-2 h-12 bg-sand/50 border-line focus-visible:ring-sage" />
          </div>
        </div>

        <div>
          <Label className="text-xs uppercase tracking-[0.2em] font-bold text-ink/70">{t.motivo}</Label>
          <Select value={form.motivo} onValueChange={(v) => setForm((f) => ({ ...f, motivo: v }))}>
            <SelectTrigger data-testid="lead-motivo-select" className="mt-2 h-12 bg-sand/50 border-line focus:ring-sage">
              <SelectValue placeholder={t.motivoPh} />
            </SelectTrigger>
            <SelectContent className="bg-white border-line">
              {t.motivos.map((m, i) => (
                <SelectItem key={m.v} value={m.v} data-testid={`lead-motivo-option-${i}`}>{m.l}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <motion.div whileTap={{ scale: 0.98 }} className="mt-8">
        <Button type="submit" disabled={loading} data-testid="submit-lead-form"
          className="w-full h-13 py-4 rounded-full bg-sage text-white text-sm font-bold tracking-wide transition-colors duration-300 hover:bg-sage-dark">
          {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <span className="inline-flex items-center gap-2"><Send className="h-4 w-4" /> {t.submit}</span>}
        </Button>
      </motion.div>
      <p className="mt-4 text-center text-xs text-stonemute" data-testid="form-privacy-note">
        {t.privacy}
      </p>
    </form>
  );
}
